/**
 * AP-39 10.4 — **Preisspiegel im Einkauf**: Zeilen sind Stücklistenzeilen mit mindestens einem
 * Angebot, je antwortendem Lieferanten eine Spalte mit dem Nettopreis je Einheit der Zeile
 * (EP ÷ PE × (1 − Rabatt)). Der günstigste ist markiert; gewählt wird je Zeile eine Zelle.
 * „Preise übernehmen" zeigt zuerst die Wirkung.
 *
 * Die Angebote stehen an der Preisanfrage im Belegwesen (Abschnitt „Antwort des Lieferanten") —
 * erfasst von Hand oder aus dem Posteingang. Wie in der Kalkulation (AP-37 O 7.3) eine
 * Vergleichsmatrix, keine Liste: Die Spalten sind die Lieferanten der Antworten.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, CheckCircle2 } from 'lucide-react'
import { geldText } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { useTerminology } from '@/lib/useTerminology'
import { applyOffers, fetchOfferComparison, type ApplyOffersResult, type OfferComparison, type OfferComparisonRow } from '../api/project-bom.api'
import { alsTag } from '../project-labels'

const mengeText = (x: number) => x.toLocaleString('de-DE', { maximumFractionDigits: 3 })
/** Nettopreis mit bis zu vier Stellen (Kleinteile), sonst im Geldformat. */
const preis = (x: number) => (Math.abs(x * 100 - Math.round(x * 100)) > 1e-6 ? x.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) : geldText(x))

/** `positionId`: nur die Zeilen einer Position — das Fenster „Angebote übernehmen" der Stückliste (AP-35 N17-2). */
export function OfferComparisonTab({ projectId, positionId, onGeaendert }: { projectId?: string; positionId?: string; onGeaendert?: () => void }) {
  const terms = useTerminology()
  const [daten, setDaten] = useState<OfferComparison | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [hinweis, setHinweis] = useState<string | null>(null)
  const [lieferant, setLieferant] = useState('')
  const [nurOffene, setNurOffene] = useState(true)
  const [wahl, setWahl] = useState<Map<string, string>>(new Map())
  const [uebernehmen, setUebernehmen] = useState(false)

  const laden = useCallback(() => {
    fetchOfferComparison({ projectId, positionId, supplierId: lieferant || undefined, open: nurOffene })
      .then((d) => {
        setDaten(d)
        setFehler(null)
        // Vorgabe: der günstigste je Zeile — nur für Zeilen, die noch keine Wahl haben.
        setWahl((alt) => {
          const neu = new Map<string, string>()
          for (const r of d.rows) {
            const bisher = alt.get(r.lineId)
            if (bisher && r.offers.some((o) => o.documentLineId === bisher)) neu.set(r.lineId, bisher)
            else if (r.bestDocumentLineId && r.bestDocumentLineId !== r.appliedDocumentLineId) neu.set(r.lineId, r.bestDocumentLineId)
          }
          return neu
        })
      })
      .catch((e) => setFehler(apiErrorText(e, 'Der Preisspiegel ließ sich nicht laden.')))
  }, [projectId, positionId, lieferant, nurOffene])
  useEffect(() => laden(), [laden])

  const lieferanten = daten?.suppliers ?? []
  const gruppen = useMemo(() => {
    const m = new Map<string, { label: string; rows: OfferComparisonRow[] }>()
    for (const r of daten?.rows ?? []) {
      const g = m.get(r.projectId) ?? { label: r.projectLabel, rows: [] }
      g.rows.push(r)
      m.set(r.projectId, g)
    }
    return [...m.entries()]
  }, [daten])

  const voreinstellung = (art: 'BEST' | 'NONE' | string) => {
    const neu = new Map<string, string>()
    for (const r of daten?.rows ?? []) {
      if (art === 'NONE') continue
      if (art === 'BEST') {
        if (r.bestDocumentLineId) neu.set(r.lineId, r.bestDocumentLineId)
        continue
      }
      const o = r.offers.find((x) => x.supplierId === art && x.netPrice !== null && x.unitMatches)
      if (o) neu.set(r.lineId, o.documentLineId)
    }
    setWahl(neu)
  }

  const auswahl = [...wahl].filter(([lineId, documentLineId]) => {
    const r = daten?.rows.find((x) => x.lineId === lineId)
    return r && r.appliedDocumentLineId !== documentLineId
  })
  const spalten = 4 + lieferanten.length

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 text-sm">
        <label className="block">
          <span className="mb-0.5 block text-xs text-muted-foreground">Lieferant</span>
          <select className="rounded-md border border-border bg-background px-2 py-1" value={lieferant} onChange={(e) => setLieferant(e.target.value)}>
            <option value="">alle</option>
            {lieferanten.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1.5 pb-1">
          <input type="checkbox" checked={nurOffene} onChange={(e) => setNurOffene(e.target.checked)} />
          nur noch nicht übernommene
        </label>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Wahl:</span>
          <button type="button" className="rounded-md border border-border px-2 py-1 text-xs hover:bg-muted" onClick={() => voreinstellung('BEST')}>
            günstigster je Zeile
          </button>
          <select className="rounded-md border border-border bg-background px-2 py-1 text-xs" value="" onChange={(e) => e.target.value && voreinstellung(e.target.value)} aria-label="alles von einem Lieferanten">
            <option value="">alles von …</option>
            {lieferanten.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
          <button type="button" className="rounded-md border border-border px-2 py-1 text-xs hover:bg-muted" onClick={() => voreinstellung('NONE')}>
            keine
          </button>
          <button type="button" className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50" disabled={!auswahl.length} onClick={() => setUebernehmen(true)}>
            {auswahl.length} Preis{auswahl.length === 1 ? '' : 'e'} übernehmen …
          </button>
        </div>
      </div>

      {fehler && <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}
      {hinweis && <p className="text-sm text-green-700 dark:text-green-400">{hinweis}</p>}

      <div className="overflow-x-auto rounded-lg border border-border bg-card shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/50 text-xs">
            <tr>
              <th className="px-3 py-1.5 text-left font-medium">{terms.position} · Material</th>
              <th className="px-3 py-1.5 text-right font-medium">Bedarf</th>
              <th className="px-3 py-1.5 text-right font-medium">jetzt</th>
              {lieferanten.map((l) => (
                <th key={l.id} className="px-3 py-1.5 text-right font-medium">
                  {l.name}
                </th>
              ))}
              <th className="px-3 py-1.5 text-right font-medium">Wirkung</th>
            </tr>
          </thead>
          <tbody>
            {daten && daten.rows.length === 0 && (
              <tr>
                <td colSpan={spalten} className="px-4 py-8 text-center text-muted-foreground">
                  {nurOffene
                    ? 'Keine Angebote, die noch nicht übernommen sind.'
                    : 'Noch keine Angebote — die Antwort des Lieferanten wird an der Preisanfrage im Schriftverkehr erfasst.'}
                </td>
              </tr>
            )}
            {gruppen.map(([id, g]) => [
              <tr key={`g-${id}`} className="border-b border-border bg-muted/40">
                <td colSpan={spalten} className="px-3 py-1 font-medium">
                  <Link to={`/projects/${id}`} className="hover:text-primary hover:underline">
                    {g.label}
                  </Link>
                </td>
              </tr>,
              ...g.rows.map((r) => {
                const gewaehlt = wahl.get(r.lineId)
                const o = r.offers.find((x) => x.documentLineId === gewaehlt)
                return (
                  <tr key={r.lineId} className="border-b border-border/60 align-top">
                    <td className="px-3 py-1.5">
                      <div>{r.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {r.positionLabel}
                        {r.material ? ` · ${r.material}` : ''}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">
                      {mengeText(r.quantity)} {r.unitCode}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">
                      {r.current.netPrice != null ? preis(r.current.netPrice) : '—'}
                      <div className="text-xs text-muted-foreground">{r.current.supplierName ?? ''}</div>
                    </td>
                    {lieferanten.map((l) => {
                      const x = r.offers.find((y) => y.supplierId === l.id)
                      if (!x) return <td key={l.id} className="px-3 py-1.5" />
                      const bestes = x.documentLineId === r.bestDocumentLineId
                      const an = gewaehlt === x.documentLineId
                      const waehlbar = x.netPrice !== null && x.unitMatches
                      return (
                        <td key={l.id} className={`px-2 py-1 text-right ${x.applied ? 'text-muted-foreground' : ''}`}>
                          {x.notOffered ? (
                            <span className="text-xs text-muted-foreground">nicht angeboten</span>
                          ) : (
                            <label className={`inline-flex cursor-pointer flex-col items-end rounded-md px-2 py-0.5 ${an ? 'bg-primary/15 ring-1 ring-primary' : 'hover:bg-muted'} ${waehlbar ? '' : 'cursor-not-allowed opacity-60'}`}>
                              <span className="flex items-center gap-1 tabular-nums">
                                <input
                                  type="radio"
                                  name={`angebot-${r.lineId}`}
                                  className="sr-only"
                                  disabled={!waehlbar || x.applied}
                                  checked={an}
                                  onChange={() => setWahl((alt) => new Map(alt).set(r.lineId, x.documentLineId))}
                                />
                                {bestes && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" aria-label="günstigster" />}
                                {x.netPrice != null ? preis(x.netPrice) : '—'}
                              </span>
                              {/* Verglichen in der Einheit der Zeile, angeboten in einer anderen (Kilopreis auf Laufmeter). */}
                              {x.converted && x.offerNetPrice != null && (
                                <span className="text-xs tabular-nums text-muted-foreground" title="angeboten in einer anderen Einheit, umgerechnet über das Gewicht">
                                  {preis(x.offerNetPrice)} je {x.offerUnitCode}
                                </span>
                              )}
                              <span className="text-xs text-muted-foreground">
                                {[x.documentNumber, x.leadTimeDays != null ? `${x.leadTimeDays} Tage` : '', x.applied ? 'übernommen' : ''].filter(Boolean).join(' · ')}
                              </span>
                              {x.validUntil && <span className="text-xs text-muted-foreground">gültig bis {alsTag(x.validUntil)}</span>}
                              {x.alternative && (
                                <span className="inline-flex items-center gap-0.5 text-xs text-amber-700 dark:text-amber-300" title={x.alternative}>
                                  <AlertTriangle className="h-3 w-3" /> Alternative
                                </span>
                              )}
                              {!x.unitMatches && <span className="text-xs text-destructive">andere Einheit — Gewicht fehlt</span>}
                            </label>
                          )}
                        </td>
                      )
                    })}
                    <td className="whitespace-nowrap px-3 py-1.5 text-right text-xs tabular-nums">
                      {o && o.costNew != null ? (
                        <>
                          {r.current.cost != null ? geldText(r.current.cost) : '—'} → <span className="font-medium">{geldText(o.costNew)}</span>
                        </>
                      ) : (
                        ''
                      )}
                    </td>
                  </tr>
                )
              }),
            ])}
          </tbody>
          {lieferanten.length > 0 && (daten?.rows.length ?? 0) > 0 && (
            <tfoot>
              <tr className="border-t-2 border-border bg-muted/50 text-xs font-medium">
                <td className="px-3 py-1.5">{daten!.rows.length} Zeilen</td>
                <td />
                <td />
                {lieferanten.map((l) => (
                  <td key={l.id} className="px-3 py-1.5 text-right tabular-nums">
                    {geldText(l.total)}
                    <div className="font-normal text-muted-foreground">{l.missing ? `${l.missing} von ${daten!.rows.length} ohne Preis` : 'alle Zeilen'}</div>
                  </td>
                ))}
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {uebernehmen && (
        <ApplyOffersDialog
          choices={auswahl.map(([lineId, documentLineId]) => ({ lineId, documentLineId }))}
          stands={(daten?.rows ?? []).map((r) => ({ lineId: r.lineId, updatedAt: r.updatedAt }))}
          onClose={() => setUebernehmen(false)}
          onFertig={(r) => {
            setUebernehmen(false)
            setHinweis(
              `${r.applied === 1 ? '1 Preis' : `${r.applied} Preise`} in die Stückliste übernommen${r.skipped.length ? ` · ${r.skipped.length} übersprungen` : ''}.`,
            )
            laden()
            onGeaendert?.()
          }}
        />
      )}
    </div>
  )
}

const GRUND: Record<ApplyOffersResult['skipped'][number]['reason'], string> = {
  ORDERED: 'schon bestellt',
  NO_PRICE: 'ohne Preis',
  NO_WEIGHT: 'Gewicht je Einheit fehlt',
  UNIT: 'andere Einheit',
  GONE: 'inzwischen geändert',
}

/**
 * Vorschau alt → neu je Zeile, dann schreiben (Handlung mit Verb, speichern.md Fall 3). Auch der Knopf an der
 * Stücklistenzeile öffnet sie — mit genau einer Wahl (AP-35 N17-1).
 */
export function ApplyOffersDialog({
  choices,
  stands,
  onClose,
  onFertig,
}: {
  choices: { lineId: string; documentLineId: string }[]
  stands: { lineId: string; updatedAt: string }[]
  onClose: () => void
  onFertig: (r: ApplyOffersResult) => void
}) {
  const [vorschau, setVorschau] = useState<ApplyOffersResult | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  useEffect(() => {
    applyOffers({ choices, dryRun: true })
      .then(setVorschau)
      .catch((e) => setFehler(apiErrorText(e, 'Die Vorschau ließ sich nicht berechnen.')))
  }, [choices])

  const gewaehlt = new Set(choices.map((c) => c.lineId))
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-label="Preise übernehmen">
      <div className="max-h-[85vh] w-full max-w-3xl space-y-3 overflow-auto rounded-lg border border-border p-5 shadow-xl" style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}>
        <h2 className="text-lg font-semibold">Preise in die Stückliste übernehmen</h2>
        <p className="text-sm text-muted-foreground">
          Geschrieben werden Lieferant, Einkaufspreis, Preiseinheit und Rabatt — und ein Liefertermin aus der Lieferzeit, wo noch keiner steht. Der Status bleibt „angefragt", bis bestellt wird.
        </p>
        {vorschau && vorschau.lines.length > 0 && (
          <table className="w-full text-sm">
            <thead className="border-b border-border text-xs text-muted-foreground">
              <tr>
                <th className="py-1 text-left font-medium">Zeile</th>
                <th className="py-1 text-left font-medium">Lieferant</th>
                <th className="py-1 text-right font-medium">Netto je Einheit</th>
                <th className="py-1 text-right font-medium">Einkauf Soll</th>
              </tr>
            </thead>
            <tbody>
              {vorschau.lines.map((l) => (
                <tr key={l.lineId} className="border-b border-border/60 align-top">
                  <td className="py-1 pr-2">
                    {l.name}
                    <div className="text-xs text-muted-foreground">
                      {l.projectLabel} · {l.positionLabel}
                    </div>
                    {l.conversion === 'IN_KG' && <div className="text-xs text-amber-700 dark:text-amber-300">Die Zeile wird in kg umgerechnet — der Lieferant bietet je kg an.</div>}
                    {l.conversion === 'ZURUECK' && <div className="text-xs text-amber-700 dark:text-amber-300">Die Zeile wird aus kg zurückgestellt — der Lieferant bietet in der ursprünglichen Einheit an.</div>}
                    {l.deliveryDueAt && <div className="text-xs text-muted-foreground">Liefertermin {alsTag(l.deliveryDueAt)}</div>}
                  </td>
                  <td className="py-1 pr-2">
                    {l.supplierName}
                    <div className="text-xs text-muted-foreground">{l.documentNumber}</div>
                  </td>
                  <td className="whitespace-nowrap py-1 text-right tabular-nums">
                    {l.netPriceBefore != null ? preis(l.netPriceBefore) : '—'} → <span className="font-medium">{l.netPriceAfter != null ? preis(l.netPriceAfter) : '—'}</span>
                  </td>
                  <td className="whitespace-nowrap py-1 text-right tabular-nums">
                    {l.costBefore != null ? geldText(l.costBefore) : '—'} → <span className="font-medium">{l.costAfter != null ? geldText(l.costAfter) : '—'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="text-sm font-medium">
                <td className="py-1.5" colSpan={3}>
                  Summe Einkauf Soll
                </td>
                <td className="whitespace-nowrap py-1.5 text-right tabular-nums">
                  {geldText(vorschau.totalBefore)} → {geldText(vorschau.totalAfter)}
                </td>
              </tr>
            </tfoot>
          </table>
        )}
        {!!vorschau?.skipped.length && (
          <div className="text-xs text-amber-700 dark:text-amber-400">Bleiben zurück: {vorschau.skipped.map((s) => `${s.name} (${GRUND[s.reason]})`).join(', ')}</div>
        )}
        {fehler && <p className="text-sm text-destructive">{fehler}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className="rounded-md border border-border px-3 py-1.5 text-sm" onClick={onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            disabled={!vorschau?.lines.length || laeuft}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
            onClick={() => {
              setLaeuft(true)
              void applyOffers({ choices, stands: stands.filter((s) => gewaehlt.has(s.lineId)) })
                .then(onFertig)
                .catch((e) => setFehler(apiErrorText(e, 'Das Übernehmen hat nicht geklappt.')))
                .finally(() => setLaeuft(false))
            }}
          >
            {vorschau?.lines.length === 1 ? '1 Preis' : `${vorschau?.lines.length ?? 0} Preise`} übernehmen
          </button>
        </div>
      </div>
    </div>
  )
}
